import React, { useState } from 'react';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_BASE;

export default function UpdatePwd() {
  const [searchParams] = useSearchParams();
  const { regToken } = useParams();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validate = () => {
    if (!formData.password) {
      setError('Password is required');
      return false;
    }

    if (formData.password.length < 6 || !/^[\x20-\x7E]+$/.test(formData.password)) {
      setError('Use 6 or more characters with a mix of letters, numbers & symbols');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || !validate()) return;

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const key = searchParams.get('key');
      const res = await fetch(`${API_BASE}/signup/${regToken}/updatePwd?key=${key}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json;charset=UTF-8'
        },
        body: JSON.stringify({
          password: formData.password
        })
      });

      if (res.ok) {
        setSuccess(true);
      } else if (res.status === 401) {
        navigate('/forgotpwd');
      } else {
        const data = await res.json();
        setError(data.message || 'Failed to update password');
      }
    } catch (err) {
      setError('Unable to connect to the server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative z-20">
      <div className="max-w-sm w-full sm:max-w-md p-4 my-8 bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)] space-y-6">
        <div className="text-center space-y-4">
          {/* Lock Icon */}
          <div className="flex justify-center mb-4">
            {success ? (
              <CheckCircle2 className="w-10 h-10 text-green-500" strokeWidth={1.5} />
            ) : (
              <Lock className="w-10 h-10 text-[var(--accent-primary)]" strokeWidth={1.5} />
            )}
          </div>
          
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            {success ? 'Password Updated Successfully!' : 'Update Password'}
          </h2>
        </div>

        {success ? (
          <div className="text-center">
            <span className="text-sm font-medium text-green-500">
              Your password has been updated successfully
            </span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-4 rounded bg-[var(--card-bg)] border border-[var(--border-color)] shadow-sm">
                <div className="flex items-center gap-3">
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="var(--accent-primary)"/>
                  </svg>
                  <span className="text-[var(--text-primary)] text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* New Password */}
            <div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full p-2 pl-10 pr-9 border rounded bg-[var(--card-bg)] focus:outline-none ${
                    error 
                      ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                      : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
                  } disabled:opacity-50`}
                  placeholder="Enter new password"
                />
                <Lock className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-2 flex items-center px-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  tabIndex={-1}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={loading}
                  className={`w-full p-2 pl-10 pr-9 border rounded bg-[var(--card-bg)] focus:outline-none ${
                    error 
                      ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                      : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
                  } disabled:opacity-50`}
                  placeholder="Confirm new password"
                />
                <Lock className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-2 flex items-center px-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  tabIndex={-1}
                  disabled={loading}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center px-4 py-2 font-semibold bg-[var(--accent-primary)] text-[var(--btn-text-on-accent)] rounded hover:bg-[var(--hover-dark)] active:bg-[var(--hover-dark)] focus-visible:bg-[var(--hover-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card-bg)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--accent-primary)]"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="w-5 h-5 border-2 border-[var(--btn-text-on-accent)] border-t-transparent rounded-full animate-spin mr-2"></div>
                  Updating Password...
                </div>
              ) : (
                'Update Password'
              )}
            </button>
          </form>
        )}

        <button
          onClick={() => navigate('/login')}
          className="flex items-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Return to Login
        </button>
      </div>
    </div>
  );
} 